import { useState } from "react";
import { Button } from "../../components/button/button";
import UserService from "../../utilities/services/user-service";
import { User } from "../../models/interfaces/user";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { createLogger } from "../../utilities/logger";

const log = createLogger("NormalizeUserEmailsTool");

const COMPONENT_CLASS = "c-normalize-user-emails-tool";

interface NormalizeRow {
    user: User;
    currentEmail: string;
    newEmail: string;
    status?: "pending" | "updated" | "skipped-duplicate" | "error";
    error?: string;
}

interface DuplicateGroup {
    email: string;
    users: User[];
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const NormalizeUserEmailsTool: React.FC = () => {
    const { state } = useAuthState();
    const [loading, setLoading] = useState(false);
    const [scanned, setScanned] = useState(false);
    const [totalUsers, setTotalUsers] = useState(0);
    const [rows, setRows] = useState<NormalizeRow[]>([]);
    const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
    const [applying, setApplying] = useState(false);
    const [appliedCount, setAppliedCount] = useState(0);

    const handleScan = async () => {
        setLoading(true);
        setScanned(false);
        setRows([]);
        setDuplicates([]);
        setAppliedCount(0);

        try {
            const users = await UserService.getAll();
            setTotalUsers(users.length);

            const byNormalized = new Map<string, User[]>();
            for (const user of users) {
                if (!user.email || user.email.trim() === "") continue;
                const normalized = normalizeEmail(user.email);
                const group = byNormalized.get(normalized) ?? [];
                group.push(user);
                byNormalized.set(normalized, group);
            }

            const dupGroups: DuplicateGroup[] = [];
            const duplicateEmails = new Set<string>();
            for (const [email, group] of byNormalized) {
                if (group.length > 1) {
                    dupGroups.push({ email, users: group });
                    duplicateEmails.add(email);
                }
            }

            const normalizeRows: NormalizeRow[] = [];
            for (const user of users) {
                if (!user.email || user.email.trim() === "") continue;
                const normalized = normalizeEmail(user.email);
                if (user.email === normalized) continue;
                normalizeRows.push({
                    user,
                    currentEmail: user.email,
                    newEmail: normalized,
                    status: duplicateEmails.has(normalized) ? "skipped-duplicate" : "pending",
                });
            }

            setDuplicates(dupGroups);
            setRows(normalizeRows);
            setScanned(true);
        } catch (error) {
            log.error("Failed to scan users", { exception: error });
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        const toFix = rows.filter((r) => r.status === "pending");
        if (toFix.length === 0) return;

        setApplying(true);
        const CHUNK_SIZE = 50;
        let updated = 0;

        for (let i = 0; i < toFix.length; i += CHUNK_SIZE) {
            const chunk = toFix.slice(i, i + CHUNK_SIZE);
            const results = await Promise.all(chunk.map(async (row) => {
                try {
                    await UserService.updateFields(row.user, { email: row.newEmail }, state.user ?? null);
                    row.status = "updated";
                    return true;
                } catch (error) {
                    row.status = "error";
                    row.error = error instanceof Error ? error.message : "Unknown error";
                    log.error("Failed to normalize email", { userId: row.user.id, exception: error });
                    return false;
                }
            }));
            updated += results.filter(Boolean).length;
            setAppliedCount(updated);
            setRows([...rows]);
        }

        setApplying(false);
    };

    const pendingCount = rows.filter((r) => r.status === "pending").length;
    const skippedCount = rows.filter((r) => r.status === "skipped-duplicate").length;
    const errorCount = rows.filter((r) => r.status === "error").length;

    return (
        <div className={COMPONENT_CLASS}>
            <h2>Normalize User Emails</h2>
            <p>
                One-time data fix: user emails are stored lowercased. Scan for mixed-case emails and
                normalize them. Case-variant duplicates are reported but never auto-merged - resolve
                those manually.
            </p>

            <div className={`${COMPONENT_CLASS}__actions`}>
                <Button
                    onClick={handleScan}
                    buttonText={loading ? "Scanning..." : "Scan Users"}
                    disabled={loading || applying}
                />
                {scanned && pendingCount > 0 && (
                    <Button
                        onClick={handleApply}
                        buttonText={applying ? `Applying... ${appliedCount}/${pendingCount}` : `Apply Normalization (${pendingCount})`}
                        disabled={applying}
                    />
                )}
            </div>

            {scanned && (
                <div className={`${COMPONENT_CLASS}__content`}>
                    <p>
                        Scanned {totalUsers} users. {rows.length} records to normalize
                        ({pendingCount} pending, {skippedCount} skipped as duplicates, {appliedCount} applied
                        {errorCount > 0 ? `, ${errorCount} failed` : ""}).
                    </p>

                    {rows.length > 0 && (
                        <>
                            <label>Records to Normalize</label>
                            <table className="light" cellSpacing={0} cellPadding={0}>
                                <thead>
                                    <tr>
                                        <th>User ID</th>
                                        <th>Current Email</th>
                                        <th>New Email</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.user.id}</td>
                                            <td>{r.currentEmail}</td>
                                            <td>{r.newEmail}</td>
                                            <td>
                                                {r.status === "updated" && "Updated"}
                                                {r.status === "skipped-duplicate" && "Skipped (duplicate - resolve manually)"}
                                                {r.status === "error" && `Error: ${r.error}`}
                                                {r.status === "pending" && "Pending"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {duplicates.length > 0 && (
                        <>
                            <label>Case-Variant Duplicate Emails (manual review required)</label>
                            <table className="light" cellSpacing={0} cellPadding={0}>
                                <thead>
                                    <tr>
                                        <th>Normalized Email</th>
                                        <th>Records</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {duplicates.map((d, i) => (
                                        <tr key={i}>
                                            <td>{d.email}</td>
                                            <td>
                                                {d.users.map((u) => (
                                                    <div key={u.id}>
                                                        {u.email ?? "(no email)"} - {u.id}
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {rows.length === 0 && duplicates.length === 0 && (
                        <p>No mixed-case emails or duplicates found.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default NormalizeUserEmailsTool;
